/**
 * OnboardingGate — Sits *inside* AuthGate's authenticated branch.
 *
 *   AuthGate → OnboardingGate
 *     ├─ has at least one organization → render <children/>
 *     └─ no organizations → render <OnboardingWizard/>
 *
 * Catches:
 *   - Brand-new email/password sign-ups (the api worker no longer
 *     auto-creates a personal workspace).
 *   - Brand-new Google OAuth sign-ups.
 *   - Returning users who somehow ended up without any membership.
 *
 * We could pull in @tanstack/react-query here (AuthProvider already uses
 * it), but the org list is fetched once per session and revalidated on
 * wizard completion — plain `useEffect` + a refresh counter keeps the
 * dependency surface of `apps/web` minimal.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { listOrganizations, setActiveOrganization, getActiveMemberRole } from './auth-api';
import { OnboardingWizard } from './OnboardingWizard';

// Routes the gate must NOT block — they're part of the post-sign-up flow
// before workspace capture (e.g. magic-link first-time users land at
// /welcome/set-password to opt into a password).
const PASSTHROUGH_PATHS = ['/welcome/set-password'];

export interface OnboardingGateProps {
  authApiBase: string;
  children: ReactNode;
}

type Status =
  | { kind: 'loading' }
  | { kind: 'has-org' }
  | { kind: 'no-org' }
  | { kind: 'error'; message: string };

export function OnboardingGate({ authApiBase, children }: OnboardingGateProps) {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  const [refreshTick, setRefreshTick] = useState(0);

  // Let pass-through routes (e.g. password setup landing page from magic-link
  // first-time sign-in) render unblocked. Read from `window.location` rather
  // than a router hook so we don't pull react-router into apps/web's deps.
  const pathname = typeof window === 'undefined' ? '' : window.location.pathname;
  const isPassthrough = PASSTHROUGH_PATHS.some((p) => pathname.endsWith(p));

  useEffect(() => {
    let cancelled = false;
    setStatus({ kind: 'loading' });

    // If the user has memberships but Better Auth's session has no
    // activeOrganizationId (typical when their session predates their
    // first membership — the session.create hook only seeds active-org
    // for sessions created *after* the first membership exists), pick
    // the first org and flip it active. Without this, /api/plugins/auth/me
    // and every org-scoped API call sees no orgId and the rbac UI thinks
    // the user is unauthenticated.
    const ensureActiveOrg = async () => {
      const orgs = await listOrganizations(authApiBase);
      if (orgs.length === 0) {
        return { kind: 'no-org' as const };
      }
      const activeRole = await getActiveMemberRole(authApiBase).catch(() => null);
      if (activeRole === null && orgs[0]) {
        try {
          await setActiveOrganization(authApiBase, orgs[0].id);
        } catch {
          // Non-fatal — fall through; the user will see a degraded but
          // functional shell, and any subsequent navigation refreshes
          // will retry on next gate mount.
        }
      }
      return { kind: 'has-org' as const };
    };

    void ensureActiveOrg().then(
      (result) => {
        if (cancelled) {
          return;
        }
        setStatus(result);
      },
      (err: unknown) => {
        if (cancelled) {
          return;
        }
        setStatus({
          kind: 'error',
          message: err instanceof Error ? err.message : String(err),
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [authApiBase, refreshTick]);

  if (isPassthrough) {
    return <>{children}</>;
  }

  if (status.kind === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-fl-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-fl-muted border-t-fl-primary" />
      </div>
    );
  }

  // Treat a network failure on the org-list call as "render the app and
  // let downstream handle it" rather than blocking the user behind a
  // confusing "no orgs" wall.
  if (status.kind === 'error') {
    console.warn('[onboarding-gate] failed to list organizations:', status.message);
    return <>{children}</>;
  }

  if (status.kind === 'no-org') {
    return (
      <OnboardingWizard authApiBase={authApiBase} onComplete={() => setRefreshTick((t) => t + 1)} />
    );
  }

  return <>{children}</>;
}
